import { memo, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Button,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
  ListItemButton,
  ListItemText,
  Chip,
} from '@mui/material';
import { MenuBook as MenuBookIcon } from '@mui/icons-material';
import { isSkillPrompt, isInstructionPrompt } from '../utils/promptBuckets';
import InlineAlert from '../../../shared/components/InlineAlert';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import type { EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import { usePromptForm } from '../hooks/usePromptForm';
import { useAgentSpecForm } from '../hooks/useAgentSpecForm';
import EditPromptPanel from './EditPromptPanel';
import AgentSpecEditor from './AgentSpecEditor';
import AgentTools from './AgentTools';
import AgentIdentity from './AgentIdentity';
import AgentCallees from './AgentCallees';
import NewAgentDialog from './NewAgentDialog';
import RetireAgentDialog from './RetireAgentDialog';
import ModelBindings from './ModelBindings';
import FormActionBar from '../../../shared/components/FormActionBar';
import WorkflowSection from './WorkflowSection';
import { useFeatureSwitch } from '../../../shared/hooks/useFeatureSwitch';
import { useWorkflowForm } from '../hooks/useWorkflowForm';
import { makeTagGroupBy } from '../../../shared/utils/tags';
import type { Prompt, PromptTypeType, AgentSpec, DefaultAgents, WorkflowSummary } from '../../../types/backend';
import { PROMPT_TYPE_LABELS } from '../../../shared/constants/promptTypes';

const promptListConfig: EntityListItemConfig<Prompt> = {
  getId: (p) => p.id,
  getPrimaryText: (p) => p.title,
  getSecondaryText: (p) => {
    const typeLabel = PROMPT_TYPE_LABELS[p.type as PromptTypeType] ?? 'Unknown';
    const ownerLabel = p.owner === 'customer' ? 'Customer' : 'Global';
    const status = p.promptVersion && p.promptVersion !== 'DRAFT' ? `v${p.promptVersion}` : 'Unpublished';
    return `${typeLabel} · ${ownerLabel} · ${status}`;
  },
  getSearchText: (p) => [p.name, p.title, ...p.tags].join(' '),
  secondaryTypographyProps: { variant: 'caption' },
};

const promptGroupBy = makeTagGroupBy<Prompt>();

const workflowListConfig: EntityListItemConfig<WorkflowSummary> = {
  getId: (w) => w.workflow_id,
  getPrimaryText: (w) => w.title,
  getSecondaryText: (w) => {
    const version = w.version === 'DRAFT' ? 'Draft' : `v${w.version}`;
    const gates = w.gate_count > 0 ? ` · ${w.gate_count} gate${w.gate_count === 1 ? '' : 's'}` : '';
    const stages = `${w.layer_count} stage${w.layer_count === 1 ? '' : 's'}`;
    return `${version} · ${w.node_count} steps · ${stages}${gates}`;
  },
  getSearchText: (w) => [w.name, w.title, w.description].join(' '),
  secondaryTypographyProps: { variant: 'caption' },
};

// Which default an agent is, if any: an investigation starts on the interactive default and a
// rule saved without an agent runs the batch default (PO 2026-10-28).
const defaultRoleOf = (spec: AgentSpec, defaults: DefaultAgents | null): 'investigation' | 'automation' | null => {
  if (!defaults) return null;
  if (spec.shape === 'interactive' && defaults.interactive === spec.agent_id) return 'investigation';
  if (spec.shape === 'batch' && defaults.batch === spec.agent_id) return 'automation';
  return null;
};

const agentListConfig = (defaults: DefaultAgents | null): EntityListItemConfig<AgentSpec> => ({
  getId: (s) => s.agent_id,
  getPrimaryText: (s) => s.display_name,
  getSecondaryText: (s) => {
    const model = s.model_id ?? 'Not configured';
    if (s.status === 'retired') return model;
    const role = defaultRoleOf(s, defaults);
    return role ? `${role === 'investigation' ? 'Investigation' : 'Automation'} agent · ${model}` : model;
  },
  secondaryTypographyProps: { variant: 'caption' },
  getBadge: (s) => (s.status === 'retired' ? <Chip label="Retired" size="small" sx={{ ml: 1 }} /> : null),
});

interface PromptStudioModalProps {
  customerId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  /** Opens straight on this workflow's draft in the editor, as an access failure asks. */
  editWorkflowId?: string;
}

const PromptStudioModal = memo<PromptStudioModalProps>(({
  customerId,
  isOpen,
  onClose,
  editWorkflowId,
}) => {
  const form = usePromptForm({ customerId, isOpen, onClose });
  const [bucket, setBucket] = useState<'instructions' | 'skills' | 'agents' | 'workflows'>('instructions');
  const isAgents = bucket === 'agents';
  // The pipeline prompt models sit beside the agents (PS-UC-17): one more entry in the Agents
  // list, shown in place of an agent's editor while it is chosen.
  const [showBindings, setShowBindings] = useState(false);
  const isBindings = isAgents && showBindings;
  // A retired agent is history, so the list leaves it out unless asked (PO 2026-10-28).
  const [showRetired, setShowRetired] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setShowBindings(false);
      setShowRetired(false);
    }
  }, [isOpen]);
  const workflowsEnabled = useFeatureSwitch('workflows');
  // One switch governs the whole feature, so the Studio bucket and the investigation surface
  // appear together. A bucket whose flag went off while it was open falls back rather than
  // rendering a selected toggle that is no longer there.
  const isWorkflows = bucket === 'workflows' && workflowsEnabled;
  // Making and retiring agents sit behind their own switch (ADR-0064); editing one does not.
  const authoring = useFeatureSwitch('agent_authoring');
  const agentForm = useAgentSpecForm({ isOpen, active: isAgents, authoring });
  const [newAgentOpen, setNewAgentOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const visibleSpecs = showRetired ? agentForm.specs : agentForm.specs.filter((s) => s.status !== 'retired');
  // A retired agent hidden while selected gives way to the first active one, so the pane never
  // shows a row the list does not.
  const { selectedSpec: selectedAgent, handleSelect: selectAgent } = agentForm;
  const firstActive = agentForm.specs.find((s) => s.status !== 'retired') ?? null;
  useEffect(() => {
    if (!showRetired && selectedAgent?.status === 'retired' && firstActive) selectAgent(firstActive);
  }, [showRetired, selectedAgent, firstActive, selectAgent]);
  const workflowForm = useWorkflowForm({ isOpen, active: isWorkflows, onClose });
  const { editWorkflow } = workflowForm;
  useEffect(() => {
    if (!isOpen || !editWorkflowId) return;
    setBucket('workflows');
    editWorkflow(editWorkflowId);
  }, [isOpen, editWorkflowId, editWorkflow]);
  // Only the list/content area shows the per-tab spinner; the tab switcher above it
  // stays mounted so the user can always switch back mid-load. For agents, gate on
  // initialLoading so the spinner shows immediately on first activate rather than
  // flashing the empty "No agents" state before load() fires.
  const contentLoading = isAgents
    ? agentForm.initialLoading
    : isWorkflows
      ? workflowForm.initialLoading
      : form.loading;

  // Leaving the Workflows bucket unmounts the editor, so every way out of it is taken through the
  // workflow form's guard: the header X, Escape, the backdrop, the footer Close and the bucket
  // toggle. On any other bucket it passes straight through.
  const closeStudio = isWorkflows ? workflowForm.closeStudio : form.handleClose;
  const switchBucket = (next: typeof bucket) =>
    isWorkflows && next !== bucket
      ? workflowForm.leaveEditor(() => setBucket(next))
      : setBucket(next);

  const bucketPrompts = form.displayedPrompts.filter(
    bucket === 'skills' ? isSkillPrompt : isInstructionPrompt,
  );

  // The agent's shell is edited in the same form the other buckets use, so it gets the same
  // editor: Write, Tools, Test and Versions, with the same publish path. Looked up by name,
  // because the spec stores the portable name and the form is keyed by id.
  const shellPrompt = agentForm.shellPrompts.find(
    (p) => p.name === agentForm.formData.system_prompt_ref,
  ) ?? null;
  const agentSpecEditor = agentForm.selectedSpec ? (
    <>
      <AgentIdentity
        agentId={agentForm.selectedSpec.agent_id}
        displayName={agentForm.formData.display_name}
        description={agentForm.formData.description}
        errors={agentForm.fieldErrors}
        retired={agentForm.readOnly}
        onChange={agentForm.handleChange}
        onRetire={authoring ? () => setRetireOpen(true) : undefined}
        defaultRole={authoring && !agentForm.readOnly && (agentForm.selectedSpec.shape === 'interactive' || agentForm.selectedSpec.shape === 'batch')
          ? {
              role: agentForm.selectedSpec.shape === 'interactive' ? 'investigation' : 'automation',
              current: defaultRoleOf(agentForm.selectedSpec, agentForm.defaults) !== null,
              busy: agentForm.settingDefault,
              onUse: agentForm.makeDefault,
            }
          : undefined}
      />
      {/* Only an interactive or batch agent consults specialists, and only with authoring on
          does the server accept a change to them (PS-UC-15 step 6, 6b). */}
      {authoring && (agentForm.selectedSpec.shape === 'interactive' || agentForm.selectedSpec.shape === 'batch') && (
        <AgentCallees
          options={agentForm.calleeOptions}
          selected={agentForm.callees}
          readOnly={agentForm.readOnly}
          onChange={agentForm.changeCallees}
        />
      )}
      <AgentSpecEditor
        formData={agentForm.formData}
        fieldErrors={agentForm.fieldErrors}
        successMessage={agentForm.successMessage}
        models={agentForm.models}
        modelsLoading={agentForm.modelsLoading}
        readOnly={agentForm.readOnly}
        onChange={agentForm.handleChange}
      />
    </>
  ) : null;

  const agentTools = agentForm.selectedSpec ? (
    <AgentTools
      loadout={agentForm.loadout}
      loadoutSelectable={agentForm.loadoutSelectable}
      loadoutWarnings={agentForm.loadoutWarnings}
      loadoutLoading={agentForm.loadoutLoading}
      readOnly={agentForm.readOnly}
      onChangeLoadout={agentForm.changeLoadout}
    />
  ) : null;

  const { handleSelect: selectPrompt } = form;
  const selectedPromptId = form.selectedPrompt?.id ?? null;
  useEffect(() => {
    if (!isAgents || !shellPrompt) return;
    if (selectedPromptId === shellPrompt.id) return;
    selectPrompt(shellPrompt);
  }, [isAgents, shellPrompt, selectedPromptId, selectPrompt]);

  return (
    <Dialog
      open={isOpen}
      onClose={closeStudio}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '94vh', maxHeight: '94vh', width: '96vw', maxWidth: '1600px' } }}
    >
      <ModalHeader icon={MenuBookIcon} title="Prompt Studio" onClose={closeStudio} desktopRecommended />

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
        <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
          <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <ToggleButtonGroup
              size="small" exclusive fullWidth value={bucket}
              onChange={(_, v) => v && switchBucket(v)}
              sx={{ mb: 1, '& .MuiToggleButton-root': { py: 0.25, textTransform: 'none' } }}
            >
              <ToggleButton value="instructions">Instructions</ToggleButton>
              <ToggleButton value="skills">Skills</ToggleButton>
              <ToggleButton value="agents">Agents</ToggleButton>
              {workflowsEnabled && (
                <ToggleButton value="workflows">Workflows</ToggleButton>
              )}
            </ToggleButtonGroup>
            {isAgents && !isBindings && (
              <FormControlLabel
                control={
                  <Checkbox checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} size="small" />
                }
                label={<Typography variant="body2">Show retired</Typography>}
              />
            )}
            {!isAgents && !isWorkflows && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.showGlobal}
                    onChange={(e) => form.setShowGlobal(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Show Cloud2 global {bucket === 'skills' ? 'skills' : 'instructions'}</Typography>}
              />
            )}
          </Box>

          {contentLoading ? (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : isWorkflows ? (
            <EntityListPanel
              entities={workflowForm.summaries}
              selectedEntity={null}
              isNew={workflowForm.mode === 'new'}
              saving={workflowForm.saving}
              config={workflowListConfig}
              addButtonText="New workflow"
              onAddNew={workflowForm.startNew}
              emptyStateText="No workflows yet"
              emptyStateHelperText="A workflow is a saved procedure an investigation can run"
              onSelect={workflowForm.handleSelect}
              searchable
            />
          ) : isAgents ? (
            <>
              <EntityListPanel
                entities={visibleSpecs}
                selectedEntity={isBindings ? null : agentForm.selectedSpec}
                isNew={false}
                saving={agentForm.saving}
                config={agentListConfig(agentForm.defaults)}
                addButtonText={authoring ? 'New agent' : undefined}
                onAddNew={authoring ? () => setNewAgentOpen(true) : undefined}
                emptyStateText="No agents"
                emptyStateHelperText="Agent specs are seeded per environment"
                onSelect={(spec) => { setShowBindings(false); agentForm.handleSelect(spec); }}
              />
              <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
                <ListItemButton
                  selected={isBindings}
                  onClick={() => setShowBindings(true)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary="Pipeline prompt models"
                    secondary="The model each non-agent prompt runs on"
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              </Box>
            </>
          ) : (
            <EntityListPanel
              entities={bucketPrompts}
              selectedEntity={form.selectedPrompt}
              isNew={form.isNewPrompt}
              saving={form.saving}
              config={promptListConfig}
              addButtonText={bucket === 'skills' ? 'New Skill' : 'New Instruction'}
              emptyStateText={bucket === 'skills' ? 'No skills yet' : 'No instructions yet'}
              emptyStateHelperText={bucket === 'skills' ? 'Create a skill the agent can pull in' : 'Create an instruction that drives an agent'}
              onSelect={form.handleSelect}
              onAddNew={() => form.handleAddNew(bucket === 'skills' ? 'skill' : undefined)}
              searchable
              groupBy={promptGroupBy}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {contentLoading ? (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : isWorkflows ? (
            <>
              {workflowForm.error && (
                <Box sx={{ mx: 3, mt: 2 }}>
                  <InlineAlert severity="error" onClose={() => workflowForm.setError(null)}>
                    {workflowForm.error}
                  </InlineAlert>
                </Box>
              )}
              <WorkflowSection form={workflowForm} />
            </>
          ) : isBindings ? (
            <ModelBindings active={isOpen && isBindings} />
          ) : isAgents ? (
            <>
              {agentForm.error && (
                <Box sx={{ mx: 3, mt: 2 }}>
                  <InlineAlert severity="error" onClose={() => agentForm.setError(null)}>
                    {agentForm.error}
                  </InlineAlert>
                  {agentForm.specs.length === 0 && (
                    <Button size="small" variant="outlined" onClick={agentForm.retry} sx={{ mt: 1 }}>
                      Retry
                    </Button>
                  )}
                </Box>
              )}
              {shellPrompt ? (
                <EditPromptPanel
                  form={form}
                  settings={agentSpecEditor}
                  extraTab={{ label: 'Tools', content: agentTools }}
                  alsoSave={{ dirty: agentForm.hasChanges, saving: agentForm.saving, save: agentForm.handleSave }}
                />
              ) : (
                // No shell resolved: the prompt pane cannot render, and with it would go the
                // only Save. An agent whose shell is missing is exactly the one that needs
                // its fields editable, so the settings carry their own save here.
                <>
                  {agentSpecEditor}
                  {agentTools}
                  {agentForm.selectedSpec && (
                    <FormActionBar
                      onSave={agentForm.handleSave}
                      saving={agentForm.saving}
                      saveDisabled={!agentForm.hasChanges}
                      saveLabel="Save"
                      size="small"
                      sx={{ px: 3, py: 1.5 }}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {form.error && (
                <InlineAlert severity="error" onClose={() => form.setError(null)} sx={{ mx: 3, mt: 2 }}>
                  {form.error}
                </InlineAlert>
              )}
              <EditPromptPanel form={form} />
            </>
          )}
        </Box>
      </DialogContent>

      <ModalFooter
        count={
          isAgents
            ? visibleSpecs.length
            : isWorkflows
              ? workflowForm.summaries.length
              : bucketPrompts.length
        }
        label={
          isAgents
            ? `agent${visibleSpecs.length !== 1 ? 's' : ''}`
            : isWorkflows
              ? `workflow${workflowForm.summaries.length !== 1 ? 's' : ''}`
              : `${bucket === 'skills' ? 'skill' : 'instruction'}${bucketPrompts.length !== 1 ? 's' : ''} displayed`
        }
        onClose={closeStudio}
        py={1.5}
        buttonSize="small"
      />

      {/* Mounted only while open, so each one starts from a clean form and reads fresh. */}
      {authoring && newAgentOpen && (
        <NewAgentDialog
          open
          onClose={() => setNewAgentOpen(false)}
          onCreated={(spec) => { setNewAgentOpen(false); setShowBindings(false); agentForm.handleSelect(spec); }}
          shapes={agentForm.shapes}
          models={agentForm.models}
          shellPrompts={agentForm.shellPrompts}
        />
      )}
      {authoring && retireOpen && agentForm.selectedSpec && (
        <RetireAgentDialog open agent={agentForm.selectedSpec} specs={agentForm.specs} onClose={() => setRetireOpen(false)} />
      )}

      <DeleteConfirmDialog
        open={form.showDeleteConfirm}
        title="Delete Prompt?"
        onCancel={form.handleDeleteCancel}
        onConfirm={form.handleDeleteConfirm}
        confirmText="Delete Prompt"
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete: <strong>{form.selectedPrompt?.title}</strong> ({form.selectedPrompt?.name})
        </Typography>
        <InlineAlert severity="warning">
          <Typography variant="body2">
            This action cannot be undone. The prompt will be permanently deleted.
          </Typography>
        </InlineAlert>
      </DeleteConfirmDialog>
    </Dialog>
  );
});

PromptStudioModal.displayName = 'PromptStudioModal';

export default PromptStudioModal;
